import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import classnames from 'classnames';
import { Button, LanguageSwitcher, MyTooltip } from '@scramble/ui';
import { EMPTY_DASH, prettyMoney } from '@scramble/utils';
import { useGetBalance } from '@/query/queries/cash/useGetBalance';
import { APP_LINKS } from '@/helpers/links';
import { calculateCash } from '@/helpers/utils';
import { changeUserLocale } from '@/helpers/locale';
import IconInfoCircle from '@scramble/design-system/icons/info-circle.svg?react';
import IconUserPlus from '@scramble/design-system/icons/user-plus-01.svg?react';
import { CalculatorLink } from './calculator';
import s from './index.module.scss';

export const TopHeader = () => {
  const { t, i18n } = useTranslation();
  const { data: balance } = useGetBalance();
  const navigate = useNavigate();
  const cash = calculateCash(balance);

  const onLocaleChange = locale => changeUserLocale(i18n, locale);

  return (
    <header className={s.wrapper}>
      <div className="g-container">
        <div className={s.content}>
          <div className={s.content_left}>
            <div className={s.balance}>
              <MyTooltip
                theme="dark"
                placement="bottom"
                renderElement={() => (
                  <div className={classnames(s.balance_item, s.total)}>
                    <span className="f-body-sm">
                      {t('components:top_header.total_bal')}
                    </span>
                    <div className={s.balance_item_sub}>
                      <span className="f-body-sm f-500">
                        {balance ? prettyMoney(balance.total) : EMPTY_DASH}
                      </span>
                      <IconInfoCircle />
                    </div>
                  </div>
                )}
                renderContent={() => (
                  <div className="f-body-sm">
                    {t('components:top_header.total_tooltip')}
                  </div>
                )}
              />
              <MyTooltip
                theme="dark"
                placement="bottom"
                disabled={!balance}
                renderElement={() => (
                  <div className={classnames(s.balance_item, s.cash)}>
                    <span className="f-body-sm">
                      {t('components:top_header.cash')}
                    </span>
                    <div className={s.balance_item_sub}>
                      <span className="f-body-sm f-500">
                        {balance ? prettyMoney(cash) : EMPTY_DASH}
                      </span>
                      <IconInfoCircle />
                    </div>
                  </div>
                )}
                renderContent={() =>
                  balance && (
                    <div className="f-body-sm">
                      <div className="f-small-regular">
                        {t('components:top_header.avail_cash', {
                          amount: prettyMoney(balance.available),
                        })}
                      </div>
                      {!!balance.on_hold && (
                        <div className="f-small-regular">
                          {balance.frozen_fee
                            ? t('components:top_header.alloc_cash_fee', {
                                amount: prettyMoney(balance.on_hold),
                                feeAmount: prettyMoney(balance.frozen_fee),
                              })
                            : t('components:top_header.alloc_cash', {
                                amount: prettyMoney(balance.on_hold),
                              })}
                        </div>
                      )}
                      {!!balance.available_bonus_amount && (
                        <div className="f-small-regular">
                          {t('components:top_header.scramble_bonus', {
                            amount: prettyMoney(balance.available_bonus_amount),
                          })}
                        </div>
                      )}
                      {!!balance.restricted && (
                        <div className="f-small-regular">
                          {t('components:top_header.restricted_cash', {
                            amount: prettyMoney(balance.restricted),
                          })}
                        </div>
                      )}
                      {!!balance.on_hold && (
                        <div
                          className="f-small-regular"
                          style={{ marginTop: '16px' }}
                        >
                          {t('components:top_header.end_round')}
                        </div>
                      )}
                    </div>
                  )
                }
              />
            </div>
            <CalculatorLink />
          </div>
          <div className={s.actions}>
            <Button
              type="button"
              theme="neutral"
              appearance="filled"
              size="md"
              className={s.btn_invite}
              onClick={() => navigate(APP_LINKS.affiliate)}
            >
              <IconUserPlus />
              <span className="f-body-sm">
                {t('components:top_header.invite')}
              </span>
            </Button>
            <LanguageSwitcher currentLocale={i18n.language} onLocaleChange={onLocaleChange} />
          </div>
        </div>
      </div>
    </header>
  );
};
